/* eslint-disable new-cap */
import React, { useState, useEffect, useRef } from "react"
import _isEmpty from "lodash/isEmpty"
import _get from "lodash/get"
import { NumericFormat } from "react-number-format"

import useWindowResize from "@/hooks/useWindowResize"
import { onImageError } from "@/utils/index"
import { getConfig } from "@/constants/config"

import Modal from "@/components/core/Modal/Modal"
import Button from "@/components/core/Button/Button"
import PDPRequiredItem from "@/components/ProductDetail/v3/PDPRequiredItem"

import styles from "@/components/ProductDetail/v3/PDPRequiredItemModal/index.module.scss"
import { pushAnalyticsData } from "@/components/ProductDetail/v3/PDPRequiredItemModal/PDPRequiredItemModalAnalytics"

const PDPRequiredItemModal = props => {
  const {
    name = "",
    desc = "",
    customerFacingSKU = "",
    sku = "",
    staticTexts = {},
    imagesData = [],
    installReq = [],
    colorsData = [],
    price = 0,
    discountedPrice = 0,
    currencySign = "",
    reqItemLwData = [],
    currencyCode = "",
    locale = "",
    productInfo = {},
    discountPercent = 0,
    isType = "",
    getRequireItemsData = () => {},
    onModalClose = () => {},
    showModal = false,
    installRequiredItemsData = [],
  } = props

  const [addItems, setAddItems] = useState([])
  const [enableInclude, setEnableInclude] = useState(false)
  const [config, setConfig] = useState("")
  const [imageUrl, setImageUrl] = useState("")

  const modalWindow = useRef(null)
  const itemScroll = useRef(null)
  const [width] = useWindowResize()

  window.addEventListener("click", function (e) {
    if (e?.target?.contains(modalWindow?.current)) {
      onModalClose()
    }
  })

  useEffect(() => {
    getConfig().then(config => setConfig(config))
  }, [])

  useEffect(() => {
    if (!_isEmpty(config)) {
      const { general: { swatchUrl = "" } = {} } = config
      const { assetId = "", url = "" } = imagesData[0] ?? {}
      const imageUrl = !url.includes("/PAWEB/")
        ? url
        : swatchUrl + assetId
      setImageUrl(imageUrl)
    }
  }, [config, imagesData])

  useEffect(() => {
    setAddItems(installReq)
  }, [installReq])

  useEffect(() => {
    if (addItems.length !== 0) {
      setEnableInclude(true)
    } else {
      setEnableInclude(false)
    }
  }, [addItems])

  const updateItems = (checked, item) => {
    let installRequireItems = addItems
    if (checked) {
      installRequireItems = installRequireItems.filter(
        requiredProduct =>
          requiredProduct.masterVariantSku !== item.masterVariantSku
      )
      installRequireItems.push(item)
    } else {
      installRequireItems = installRequireItems.filter(
        requiredProduct => requiredProduct.skuid !== item.skuid
      )
    }
    setAddItems([...installRequireItems])
  }

  const includeOnClick = () => {
    getRequireItemsData(addItems)
    onModalClose()
  }

  useEffect(() => {
    if (
      width > 991 &&
      showModal &&
      itemScroll.current &&
      installRequiredItemsData?.length > 0
    ) {
      window.OverlayScrollbars
        ? window.OverlayScrollbars(itemScroll?.current, {
            className: "os-theme-dark",
            resize: "none",
            sizeAutoCapable: true,
            clipAlways: true,
            normalizeRTL: true,
            paddingAbsolute: false,
            autoUpdate: null,
            autoUpdateInterval: 33,
            nativeScrollbarsOverlaid: {
              showNativeScrollbars: false,
              initialize: true,
            },
            overflow: {
              x: "hidden",
            },
            scrollbars: {
              visibility: "auto",
              autoHide: "never",
              autoHideDelay: 800,
              dragScrolling: true,
              clickScrolling: true,
              touchSupport: true,
              snapHandle: true,
            },
          })
        : null
    }
  }, [showModal, itemScroll?.current, installRequiredItemsData, width])

  const includeButtonClickHandler = () => {
    includeOnClick()
    pushAnalyticsData(staticTexts?.includeTextCTA?.toLowerCase(), productInfo)
  }

  return (
    <div className={styles.pdpRequiredItemModalWrapper}>
      <div className="product-detail-page__install-items-modal">
        <Modal showModal={showModal} onModalClose={onModalClose}>
          <div className="required-item" ref={modalWindow}>
            <div className="required-item__container">
              <div className="required-item__left-section">
                <div className="product-title">{staticTexts.title}</div>
                <div className="item-details">
                  <div className="item-details__image">
                    <div className="product-image">
                      <img
                        className="product-image-container"
                        src={imageUrl}
                        alt={name}
                        onError={e => onImageError(e, imageUrl)}
                        width={"250px"}
                        height={"250px"}
                      />
                    </div>
                  </div>

                  <div className="item-details__info">
                    <div className="product-family-name">{name}</div>
                    <div className="product-description">{desc}</div>
                    <div className="sku-id">{customerFacingSKU || sku}</div>
                    {colorsData.filter(c => !c.notApplicable).length > 0 && (
                      <div className="color-finish">
                        <span>{"Color/Finish"}</span> :{" "}
                        <span>
                          {_get(
                            colorsData.find(item => item.id === sku),
                            "label",
                            ""
                          )}
                        </span>
                      </div>
                    )}
                    {price > 0 && (
                      <div className="price">
                        <span className="price-amount">
                          <NumericFormat
                            value={
                              discountedPrice > 0 && discountedPrice < price ? discountedPrice : price
                            }
                            displayType="text"
                            thousandSeparator={true}
                            prefix={currencySign}
                            decimalScale={2}
                            fixedDecimalScale={true}
                          />
                        </span>
                      </div>
                    )}
                    {discountedPrice > 0 && discountedPrice < price && (
                      <div className="required-item__display-discount --mobile">
                        {discountPercent > 0 && (
                          <span className="required-item__percent-off">
                            {isType === "absolute"
                              ? currencySign + discountPercent
                              : discountPercent + "%"}{" "}
                            {staticTexts.percentOff}
                          </span>
                        )}
                        <span className="required-item__discount-price --mobile">
                          <NumericFormat
                            value={price}
                            displayType="text"
                            thousandSeparator={true}
                            prefix={currencySign}
                            decimalScale={2}
                            fixedDecimalScale={true}
                          />
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="required-item__right-section">
                <h2 className="required-product-title">
                  {staticTexts.requiredProductTitle}
                </h2>

                <div className="required-product-description">
                  {staticTexts.requiredProductDescription}
                </div>

                <div className="required-product-header">
                  <div>{staticTexts.productText}</div>
                  <div>{staticTexts.includeTextCTA}</div>
                </div>

                <div ref={itemScroll} className="product-line-items">
                  {installRequiredItemsData.length > 0 &&
                    installRequiredItemsData.map((product, i) => {
                      const lwProdData =
                        reqItemLwData?.find(i => product.id === i.ctId_s) ?? {}
                      return (
                        <PDPRequiredItem
                          staticTexts={staticTexts}
                          key={i}
                          addItemsData={addItems}
                          item={product}
                          updateItems={(checked, item) =>
                            updateItems(checked, item)
                          }
                          currencyCode={currencyCode}
                          currencySign={currencySign}
                          locale={locale}
                          colorId={i}
                          backorder={
                            lwProdData["RegionOnBackOrder_s"] === "Yes"
                          }
                          lwProdData={lwProdData}
                          productInfo={productInfo}
                          {...props}
                        />
                      )
                    })}
                </div>

                <div className="include-button">
                  <Button
                    useDefaultClass={true}
                    role="button"
                    label={staticTexts.includeTextCTA}
                    size="medium"
                    type="primary"
                    onClick={() => {
                      includeButtonClickHandler()
                    }}
                    disabled={!enableInclude}
                  ></Button>
                </div>
              </div>
            </div>
          </div>
        </Modal>
      </div>
    </div>
  )
}

export default PDPRequiredItemModal
